import { z } from "zod";

export const PORTAL_PUBLIC_WARNING = "Confidentiality warning: originals are publicly accessible. Revocation blocks portal access but cannot revoke public originals or previously downloaded files.";
export const portalKeySchema = z.string().min(1).max(1024).refine(key =>
  !key.startsWith("/") && !/[\\\x00-\x1f\x7f?#]/.test(key) &&
  key.split("/").every(segment => segment !== "." && segment !== ".." && !!segment));
const label = z.string().trim().min(1).max(255).refine(value => !/[\x00-\x1f\x7f<>]/.test(value));
export const portalBrandingSchema = z.object({
  name: label,
  logoKey: portalKeySchema.nullable(),
  website: z.string().url().max(2048).refine(value => {
    const url = new URL(value);
    return url.protocol === "https:" && !url.username && !url.password && !url.search && !url.hash;
  }).nullable(),
}).strict();
export const portalGroupsSchema = z.array(z.object({ id: z.string().min(1).max(100), name: label }).strict()).min(1).max(30)
  .refine(groups => new Set(groups.map(group => group.id)).size === groups.length);
export const portalContactSchema = z.object({ name: label, email: z.string().email().max(320) }).strict().nullable();
export const portalStorageModeSchema = z.enum(["private_copies", "public_originals"]);
export const portalPublishViewSchema = z.object({
  canPublish: z.boolean(), warning: z.string(),
  storageMode: portalStorageModeSchema,
  destinationBucket: z.string(),
  delivery: z.object({
    id: z.string(), status: z.enum(["preparing", "published", "revoked"]),
    errorCode: z.string().nullable(),
  }).nullable(),
  branding: portalBrandingSchema.nullable(), groups: portalGroupsSchema.optional(),
  assets: z.array(z.object({
    id: z.string(), name: z.string(), personaId: z.string().nullable(),
    tool: z.string(), type: z.enum(["image", "video", "logo"]), sizeBytes: z.number().nullable(),
  })).max(15001),
});
export type PortalPublishView = z.infer<typeof portalPublishViewSchema>;

/** A single safe path component. IDs, not labels, provide collision resistance. */
export function portalFilename(value: string): string {
  return value.normalize("NFKD").replace(/[^a-zA-Z0-9._-]+/g, "_")
    .replace(/\.{2,}/g, "_").replace(/^\.+/, "").slice(0, 100) || "asset";
}
